import React from 'react';

import * as RadixCheckbox from '@radix-ui/react-checkbox';
import type { CheckboxProps as RadixCheckboxProps } from '@radix-ui/react-checkbox';
import IconCheckmark from '@repo/ui-icons/IconCheckmark';
import IconMinus from '@repo/ui-icons/IconMinus';
import cn from 'classnames';

import { BaseComponentProps } from '../../../types/common';
import { H6, H5 } from '../Typography';

import styles from './Checkbox.module.css';

export type CheckboxProps = BaseComponentProps & {
  /**
   * The size of the checkbox
   */
  size?: 's' | 'm';

  /**
   * The checked state of the checkbox
   */
  checked?: RadixCheckboxProps['checked'];

  /**
   * The callback function that is called when the checkbox is checked or unchecked
   */
  onCheckedChange?: RadixCheckboxProps['onCheckedChange'];

  /**
   * Whether the checkbox is disabled
   */
  disabled?: RadixCheckboxProps['disabled'];

  /**
   * The tabindex of the checkbox
   */
  tabIndex?: number;

  /**
   * Visual state of the checkbox:
   * - `default`: Standard checkbox appearance
   * - `error`: Shows error state
   */
  state?: 'default' | 'error';
} & (
    | {
        /**
         * The unique identifier of the checkbox
         * Please, make sure to pass the unique `id` prop to the component
         */
        id: string;
        /**
         * Label text to display near the checkbox
         * In that case, the `id` prop is required to ensure the label is associated with the checkbox
         */
        label: React.ReactNode;
        ariaLabel?: never;
      }
    | {
        id?: never;
        label?: never;
        /**
         * Required aria-label when no visible label is provided
         */
        ariaLabel: string;
      }
  );

const labelComponentMap = {
  s: H6,
  m: H5,
} as const;

/**
 * Component with proper styling for checkboxes.
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  id,
  state = 'default',
  checked,
  onCheckedChange,
  size = 's',
  label,
  ariaLabel,
  dataTestId,
  disabled,
  tabIndex = 0,
}) => {
  const Icon = checked === 'indeterminate' ? IconMinus : IconCheckmark;

  const LabelComponent = labelComponentMap[size];

  return (
    <div
      aria-disabled={disabled}
      className={cn(styles.checkbox, styles[size])}
      data-testid={dataTestId}
    >
      <div className={styles.container}>
        <RadixCheckbox.Root
          aria-label={ariaLabel}
          checked={checked}
          className={cn(
            styles['radix-checkbox-root'],
            state === 'error' && styles.error,
            checked === true || checked === 'indeterminate'
              ? styles.checked
              : styles.unchecked
          )}
          disabled={disabled}
          id={id}
          tabIndex={tabIndex}
          onCheckedChange={onCheckedChange}
        >
          <RadixCheckbox.Indicator className={styles.indicator}>
            <Icon color="currentColor" />
          </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>
      </div>

      {label && (
        <label className={styles.label} htmlFor={id}>
          <LabelComponent as="span" color={disabled ? 'disabled' : 'default'}>
            {label}
          </LabelComponent>
        </label>
      )}
    </div>
  );
};
